import { Navigate, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTalentMapAccess } from '@/hooks/usePerformance';
import { cn } from '@/lib/utils';

/**
 * Shared frame for the three Talent Map views (spec "Mapa de talento.docx"):
 * pill tab bar — Evaluación individual | Mapa del equipo | Histórico.
 * Tab links keep the currently selected person when the URL carries /:email.
 * (Child-route params aren't visible via useParams in a parent layout, so the
 * email segment is read from the pathname — it stays URL-encoded as-is.)
 */
export function TalentMapLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: access } = useTalentMapAccess();
  const emailSegment = location.pathname.match(/\/performance\/talent-map\/(?:evaluation|history)\/([^/]+)/)?.[1] ?? '';
  const suffix = emailSegment ? `/${emailSegment}` : '';

  // Patrón `show` (igual que DTalkLayout): visibilidad por rol y mecanismo
  // estándar para ocultar tabs a futuro sin borrarlas.
  const tabs = [
    { to: `evaluation${suffix}`, base: 'evaluation', label: t('performance.talent_map.tabs.individual'), show: true },
    { to: 'team',                base: 'team',       label: t('performance.talent_map.tabs.team'),       show: true },
    { to: `history${suffix}`,    base: 'history',    label: t('performance.talent_map.tabs.history'),    show: true },
    // Analytics people (doc 02/09): visual agregada de compañía, solo People/HRBP/Admin
    { to: 'analytics',           base: 'analytics',  label: t('performance.talent_map.tabs.analytics', 'Analytics people'), show: !!access?.canViewAll },
  ].filter(tab => tab.show);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
      <div className="flex flex-wrap gap-2">
        {tabs.map(tab => (
          <NavLink
            key={tab.base}
            to={tab.to}
            className={({ isActive }) => cn(
              'rounded-full px-5 py-2 text-sm font-semibold transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground shadow-xs'
                : 'border border-border text-muted-foreground hover:text-foreground hover:bg-accent',
            )}
          >
            {tab.label}
          </NavLink>
        ))}
      </div>
      <Outlet />
    </div>
  );
}

/** Redirects legacy /performance/talent-map/:email bookmarks to the evaluation tab */
export function LegacyTalentMapRedirect() {
  const { email } = useParams<{ email: string }>();
  const target = email
    ? `/performance/talent-map/evaluation/${encodeURIComponent(email)}`
    : '/performance/talent-map/team';
  return <Navigate to={target} replace />;
}
